import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

import type { ICalendarStoreState } from '@/modules/calendar/types/ICalendarStoreState'

export const useCalendarStore = defineStore('calendarStore', {
  state: (): ICalendarStoreState => ({
    availableCalendars: [],
    selectedCalendars: [],
    events: [],
    totalEventsCount: 0,
    currentSortString: null,
    filters: {
      calendars: [],
      departments: [],
      status: [],
      risk: [],
      service: [],
      priority: [],
      search: '',
    },
    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
  }),

  getters: {
    // выбранные календари как объекты
    selectedCalendarObjects: state =>
      state.availableCalendars.filter(c =>
        state.selectedCalendars.includes(c.label),
      ),

    // выбран один календарь
    isSingleCalendarSelected: state =>
      state.selectedCalendars.length === 1,

    // выбранные все календари
    isAllCalendarsSelected: state =>
      state.selectedCalendars.length === state.availableCalendars.length,

    activeCalendarModule: state => {
      if (state.selectedCalendars.length !== 1) return null

      const label = state.selectedCalendars[0]

      if (label === 'Нежелательные события') return 'adverse'
      if (label === 'Тикеты') return 'ticket'

      return null
    },

    activeFilterChips: state => {
      const chips: Array<{ label: string; type: string; value?: any }> = []

      const findLabelRecursive = (nodes: any[], id: any): string | undefined => {
        if (!nodes || !Array.isArray(nodes)) return undefined

        for (const node of nodes) {
          if (String(node.id) === String(id)) {
            return node.label
          }
          if (node.children && node.children.length > 0) {
            const found = findLabelRecursive(node.children, id)
            if (found) return found
          }
        }
        return undefined
      }

      const getLabel = (field: string, id: any) => {
        let label = findLabelRecursive(state.searchChoices[field], id)
        if (!label) {
          label = findLabelRecursive(state.filterChoices[field], id)
        }
        return label || String(id)
      }

      // Календари
      if (state.filters.calendars && state.filters.calendars.length > 0) {
        state.filters.calendars.forEach((calendarId: string) => {
          chips.push({
            label: `Календарь: ${getLabel('calendars', calendarId)}`,
            type: 'calendars',
            value: calendarId,
          })
        })
      }

      // Отделения
      if (state.filters.departments && state.filters.departments.length > 0) {
        state.filters.departments.forEach((deptId: string) => {
          chips.push({
            label: `Отделение: ${getLabel('departments', deptId)}`,
            type: 'departments',
            value: deptId,
          })
        })
      }

      // Статусы
      if (state.filters.status && state.filters.status.length > 0) {
        state.filters.status.forEach((statusValue: string) => {
          chips.push({
            label: `Статус: ${getLabel('status', statusValue)}`,
            type: 'status',
            value: statusValue,
          })
        })
      }

      // Риски
      if (state.filters.risk && state.filters.risk.length > 0) {
        state.filters.risk.forEach((riskValue: string) => {
          chips.push({
            label: `Риск: ${getLabel('risk', riskValue)}`,
            type: 'risk',
            value: riskValue,
          })
        })
      }

      // Службы
      if (state.filters.service && state.filters.service.length > 0) {
        state.filters.service.forEach((serviceId: string) => {
          chips.push({
            label: `Служба: ${getLabel('service', serviceId)}`,
            type: 'service',
            value: serviceId,
          })
        })
      }

      // Приоритеты
      if (state.filters.priority && state.filters.priority.length > 0) {
        state.filters.priority.forEach((priorityValue: string) => {
          chips.push({
            label: `Приоритет: ${getLabel('priority', priorityValue)}`,
            type: 'priority',
            value: priorityValue,
          })
        })
      }

      // Поиск
      if (state.filters.search) {
        chips.push({
          label: `Поиск: ${state.filters.search}`,
          type: 'search',
        })
      }

      return chips
    },
  },

  actions: {
    /**
     * Получить список всех календарей
     * GET /api/calendar/calendar/
     */
    async fetchCalendars() {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get(`${BASE_URL}/api/calendar/calendar/`)
        this.availableCalendars = response.data.results
        // По умолчанию выбираем все календари
        this.selectedCalendars = this.availableCalendars.map(cal => cal.label)
      } catch (error) {
        const message = getApiError(error, 'Не удалось загрузить календари')
        console.error('Ошибка загрузки календарей:', error)
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Получить список событий календаря с фильтрацией
     * GET /api/calendar/events/
     */
    async fetchEvents(startDate?: string, endDate?: string) {
      if (this.selectedCalendars.length === 0) {
        this.events = []
        this.totalEventsCount = 0
        return
      }

      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = {}

        // Параметры диапазона дат (FullCalendar)
        if (startDate) params.start_date = startDate
        if (endDate) params.end_date = endDate

        // Фильтр по календарям
        if (this.selectedCalendars.length > 0) {
          if (this.selectedCalendars.length === this.availableCalendars.length) {
            params.calendars = 'all'
          } else {
            params.calendars = this.selectedCalendars.join(',')
          }
        }

        // Применяем остальные фильтры
        const { filters } = this
        if (filters.search) params.search = filters.search
        if (filters.departments?.length) params.departments = filters.departments.join(',')
        if (filters.status?.length) params.status = filters.status.join(',')
        if (filters.risk?.length) params.risk_level = filters.risk.join(',')
        if (filters.service?.length) params.service = filters.service.join(',')
        if (filters.priority?.length) params.priority = filters.priority.join(',')

        const response = await axios.get(`${BASE_URL}/api/calendar/events/`, { params })

        this.totalEventsCount = response.data.count

        this.events = response.data.results
      } catch (error) {
        const message = getApiError(error, 'Не удалось загрузить события календаря')
        console.error('Ошибка загрузки событий календаря:', error)
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загрузка вариантов фильтрации для UniversalFilterTree
     */
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        const params: any = { field: fieldKey === 'risk' ? 'risk_level' : fieldKey }
        if (search) params.search_field = search
        if (cursor) params.cursor = cursor

        let endpoint = ''
        let module = ''

        const activeModule = this.activeCalendarModule

        // определяем модуль по типу поля
        if (['departments', 'status', 'risk'].includes(fieldKey)) {
          module = activeModule === 'adverse' ? 'adverse-event' : ''
        } else if (['service', 'priority'].includes(fieldKey)) {
          module = activeModule === 'ticket' ? 'ticket' : ''
        } else if (fieldKey === 'calendars') {
          module = 'calendar'
        }

        // если модуль не определён — не делаем запрос
        if (!module) return

        // подмешивание активных фильтров (кроме текущего поля)
        const filters = this.filters

        if (fieldKey !== 'calendars' && filters.calendars?.length) {
          params.calendars = filters.calendars.join(',')
        }
        if (fieldKey !== 'departments' && filters.departments?.length) {
          params.departments = filters.departments.join(',')
        }
        if (fieldKey !== 'status' && filters.status?.length) {
          params.status = filters.status.join(',')
        }
        if (fieldKey !== 'risk' && filters.risk?.length) {
          params.risk_level = filters.risk.join(',')
        }
        if (fieldKey !== 'service' && filters.service?.length) {
          params.service = filters.service.join(',')
        }
        if (fieldKey !== 'priority' && filters.priority?.length) {
          params.priority = filters.priority.join(',')
        }
        if (filters.search) {
          params.search = filters.search
        }

        // endpoint по модулю
        if (module === 'calendar') {
          endpoint = `${BASE_URL}/api/calendar/calendar/filter-choices/`
        } else if (module === 'adverse-event') {
          endpoint = `${BASE_URL}/api/adverse-events/filter-choices/`
        } else if (module === 'ticket') {
          endpoint = `${BASE_URL}/api/ticket/ticket/filter-choices/`
        }

        const response = await axios.get(endpoint, { params })
        const { results, next, strategy } = response.data

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }

        this.filterCursors[fieldKey] = next
      } catch (e) {
        console.error(`Ошибка загрузки фильтров календаря [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    /**
     * Сброс всех фильтров
     */
    resetFilters() {
      this.filters = {
        calendars: [],
        departments: [],
        status: [],
        risk: [],
        service: [],
        priority: [],
        search: '',
      }
      this.fetchEvents()
    },

    /**
     * Удаление конкретного фильтра
     */
    removeFilter(type: string, value?: any) {
      const filters = this.filters as Record<string, any>

      switch (type) {
        case 'calendars':
          filters.calendars = filters.calendars?.filter((v: string) => v !== value) || []
          break
        case 'departments':
          filters.departments = filters.departments?.filter((v: string) => v !== value) || []
          break
        case 'status':
          filters.status = filters.status?.filter((v: string) => v !== value) || []
          break
        case 'risk':
          filters.risk = filters.risk?.filter((v: string) => v !== value) || []
          break
        case 'service':
          filters.service = filters.service?.filter((v: string) => v !== value) || []
          break
        case 'priority':
          filters.priority = filters.priority?.filter((v: string) => v !== value) || []
          break
        case 'search':
          filters.search = ''
          break
      }
      this.fetchEvents()
    },
  },
})
